import { AfterViewInit, Component, DOCUMENT, inject, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Data, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { L10nStatic } from './shared/static/l10n.static';
import { APP_POSTFIX, LS_POPUP_CHECK_ON_STARTUP, LS_USER, LS_USER_ROLE_ID, LS_USER_ROLE_ID_TEST } from './const';

import { environment } from '../environments/environment';
import { PrimeNG } from 'primeng/config';
import { UIToast } from './shared/models/toast.models';
import { ProgressLoadingService } from './common/services/progress-loading.service';
import { Toast } from 'primeng/toast';
import { BlockUI } from 'primeng/blockui';
import { ProgressSpinner } from 'primeng/progressspinner';
import { SessionService } from './shared/services/session.service';
import { PipesModule } from './shared/pipes/pipes.module';
import { MessageService, PrimeTemplate } from 'primeng/api';
import { Encryption } from './shared/services/encryption.service';
import { VersionCheckService } from './shared/services/version-check.service';
import { ConfirmDialogKeys } from './common/confirm-dialog-keys';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Button } from 'primeng/button';

@Component({
	selector: 'ui-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	encapsulation: ViewEncapsulation.None,
	imports: [RouterOutlet, Toast, BlockUI, ProgressSpinner, PipesModule, ConfirmDialog, PrimeTemplate, Button],
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
	private static readonly POPUP_CHECKED_SESSION_KEY = 'app_popup_permission_checked';
	private static readonly POPUP_CHECK_RESULT_SESSION_KEY = 'app_popup_permission_result';
	private readonly messageService = inject(MessageService);
	protected ToastKey = UIToast.ToastKey;
	protected readonly ConfirmDialogKeys = ConfirmDialogKeys;
	private destroy$: Subject<void> = new Subject();
	private readonly versionCheckService = inject(VersionCheckService);

	constructor(
    protected readonly sessionService: SessionService,
    protected readonly progressLoadingService: ProgressLoadingService,
    private router: Router,
    private route: ActivatedRoute,
    private title: Title,
    private primeNG: PrimeNG,
    @Inject(DOCUMENT) private _document: Document,
	) {
		_document.title = L10nStatic.translate('app.loading');
		this.primeNG.ripple.set(true);
		this.primeNG.overlayOptions.appendTo = 'body';

		if (localStorage.getItem(LS_USER)) {
			localStorage.removeItem(LS_USER);
			localStorage.removeItem(LS_USER_ROLE_ID);
			localStorage.removeItem(LS_USER_ROLE_ID_TEST);
		}
	}

	ngOnInit(): void {
		// for display current version after starting application
		console.log('');
		console.warn('App is started: ', this.sessionService.appVersion);
		console.log('');

		this.versionCheckService.start();

		this.router.events
			.pipe(
				takeUntil(this.destroy$),
				filter((event) => event instanceof NavigationEnd),
				switchMap(() => {
					const req = (child: ActivatedRoute): ActivatedRoute =>
						child && child.firstChild ? req(child.firstChild) : child;
					return req(this.route.firstChild).data;
				}),
				map((data: Data) => data.title || ''),
			)
			.subscribe((title: string) => {
				const postFix = APP_POSTFIX;
				const $el = this._document.getElementById('appFavicon');
				this.title.setTitle(`${title ? L10nStatic.translate(title) + ' - ' : ''}${postFix}`);
				this.setIcon($el, 'genius/newGenius/genius-only-logo.svg');
				this.sessionService.tokenService.initTokenManagement(false, true);
			});
		this.initYandex();
	}

	ngAfterViewInit(): void {
		if (!this.shouldCheckPopupPermissionOnStartup()) return;
		setTimeout(() => this.checkPopupPermissionAndPersist(), 0);
	}

	private setIcon = (element, path: string): void => {
		if (element) {
			element.setAttribute('href', `/assets/${path}`);
		}
	};

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.unsubscribe();
	}

	private initYandex(): void {
		if (environment.production) {
			const script = document.createElement('script');
			script.src = '../assets/yandex.js';
			script.async = true;
			document.body.appendChild(script);
		}
	}

	private checkPopupPermissionAndPersist(): void {
		const wnd = this._document.defaultView;
		if (!wnd) return;

		let popup: Window | null = null;
		try {
			popup = wnd.open('', '_blank', 'width=100,height=100,left=-10000,top=-10000');
		} catch {
			popup = null;
		}

		const isBlocked = !popup || popup.closed || typeof popup.closed === 'undefined';
		const result: 'allowed' | 'blocked' = isBlocked ? 'blocked' : 'allowed';

		if (isBlocked) {
			this.messageService.add({
				key: UIToast.ToastKey.appComponentBottomNotificator,
				severity: 'info',
				detail: L10nStatic.translate('app.popup_is_blocked'),
				summary: L10nStatic.translate('app.popup_is_blocked.message'),
				closable: true,
				sticky: true,
			});
		} else {
			popup.blur();
			wnd.focus();
			setTimeout(() => popup?.close(), 0);
		}

		try {
			wnd.sessionStorage.setItem(AppComponent.POPUP_CHECKED_SESSION_KEY, '1');
			wnd.sessionStorage.setItem(AppComponent.POPUP_CHECK_RESULT_SESSION_KEY, result);
		} catch {
			// ignore storage write errors
		}
	}

	private shouldCheckPopupPermissionOnStartup(): boolean {
		const value = Encryption.getLsValue<boolean>(LS_POPUP_CHECK_ON_STARTUP);
		return value !== false;
	}

}
